import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import ArtistFilters from '../components/ArtistFilters';

const SORT_LABELS = { newest: 'récents', name: 'A → Z', distance: 'distance' };

export default function ArtistList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [genres, setGenres] = useState([]);
  const [total, setTotal] = useState(0);

  const page = parseInt(searchParams.get('page') || '1', 10);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    genre: searchParams.get('genre') || '',
    city: searchParams.get('city') || '',
    country: searchParams.get('country') || '',
    availableFrom: searchParams.get('availableFrom') || '',
    availableTo: searchParams.get('availableTo') || '',
    sort: searchParams.get('sort') || 'newest',
    latitude: searchParams.get('latitude') ? parseFloat(searchParams.get('latitude')) : null,
    longitude: searchParams.get('longitude') ? parseFloat(searchParams.get('longitude')) : null,
  });

  // Fetch genres on mount
  useEffect(() => {
    async function fetchGenres() {
      try {
        const data = await api.get('/artists/genres');
        setGenres(data.data.genres || []);
      } catch (err) {
        console.error('Failed to fetch genres:', err);
      }
    }
    fetchGenres();
  }, []);

  // Fetch artists with current filters and page
  const fetchArtists = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '12');

      if (filters.search) params.append('search', filters.search);
      if (filters.genre) params.append('genre', filters.genre);
      if (filters.city) params.append('city', filters.city);
      if (filters.country) params.append('country', filters.country);
      if (filters.availableFrom) params.append('availableFrom', filters.availableFrom);
      if (filters.availableTo) params.append('availableTo', filters.availableTo);
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.latitude != null) params.append('latitude', filters.latitude.toString());
      if (filters.longitude != null) params.append('longitude', filters.longitude.toString());

      const data = await api.get(`/artists?${params.toString()}`);
      setArtists(data.data.artists || []);
      setTotal(data.data.pagination?.total || 0);

      setSearchParams(params, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // setSearchParams volontairement hors deps : son identité change à chaque
    // navigation ; l'inclure provoquerait une boucle fetch → setSearchParams → re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchArtists();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchArtists]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setSearchParams(new URLSearchParams());
  };

  const handleReset = () => {
    setSearchParams(new URLSearchParams());
  };

  const totalPages = Math.ceil(total / 12);

  // Récapitulatif lisible des filtres actifs (ligne de résultats)
  const activeCount =
    [filters.search, filters.genre, filters.city, filters.country, filters.availableFrom, filters.availableTo].filter(
      Boolean
    ).length + (filters.latitude != null ? 1 : 0);

  const summaryParts = [];
  if (filters.genre) {
    const g = genres.find((x) => x.slug === filters.genre);
    if (g) summaryParts.push(g.name.toLowerCase());
  }
  if (filters.city) summaryParts.push(filters.city);
  if (filters.availableFrom || filters.availableTo) {
    summaryParts.push(`dispo ${filters.availableFrom || '...'} → ${filters.availableTo || '...'}`);
  }
  if (filters.latitude != null) summaryParts.push('autour de moi');
  const summary = summaryParts.join(' · ');

  const showDistance = filters.sort === 'distance';

  return (
    <div className="animate-fadeIn">
      {/* Tête d'annuaire */}
      <div className="border-b th-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="th-kicker mb-3">Annuaire</div>
            <h1 className="th-display-xl">Artistes</h1>
          </div>
          <div className="th-mono-meta text-right leading-loose">
            <span className="th-text">{total} artiste{total !== 1 ? 's' : ''}</span>
            <br />
            Page {page} / {totalPages || 1} · tri : {SORT_LABELS[filters.sort] || filters.sort}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[300px_1fr]">
          <ArtistFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            genres={genres}
            onReset={handleReset}
          />

          <main className="lg:pl-8 py-8 pb-28 lg:pb-12 min-w-0">
            {/* Ligne de résultats */}
            <div className="flex items-baseline justify-between gap-4 mb-6" aria-live="polite">
              <span className="th-mono-meta normal-case">
                <span className="text-accent">{total} résultat{total !== 1 ? 's' : ''}</span>
                {summary ? ` · ${summary}` : ''}
              </span>
              {activeCount > 0 && (
                <span className="th-mono-meta whitespace-nowrap">
                  {activeCount} filtre{activeCount !== 1 ? 's' : ''} actif{activeCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px th-bg-surface border th-border">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="th-bg p-0 animate-pulse">
                    <div className="w-full aspect-square th-bg-elevated" />
                    <div className="p-4">
                      <div className="h-3 th-bg-elevated w-3/4 mb-2" />
                      <div className="h-3 th-bg-elevated w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="border border-status-rejected/30 text-status-rejected px-4 py-3 text-sm font-mono">
                {error}
              </div>
            ) : artists.length === 0 ? (
              <div className="border border-dashed th-border p-14 text-center">
                <div className="th-kicker mb-3">Aucun résultat</div>
                <div className="th-display-md mb-3">Silence radio.</div>
                <p className="th-mono-meta normal-case leading-loose mb-6 max-w-md mx-auto">
                  Aucun artiste ne correspond à ces filtres. Élargissez le rayon ou retirez un filtre.
                </p>
                <button onClick={handleReset} className="th-btn">
                  Réinitialiser les filtres
                </button>
              </div>
            ) : (
              <>
                {/* Grille feuille de contact */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-px th-bg-surface border th-border">
                  {artists.map((artist, i) => (
                    <Link
                      key={artist.id}
                      to={`/artists/${artist.slug}`}
                      className="group th-bg no-underline animate-fadeInUp"
                      style={{ animationDelay: `${0.03 * i}s` }}
                    >
                      {artist.profilePhotoUrl ? (
                        <img
                          src={artist.profilePhotoUrl}
                          alt={artist.artistName}
                          className="w-full aspect-square th-photo-duotone"
                        />
                      ) : (
                        <div className="w-full aspect-square th-bg-elevated flex items-center justify-center font-serif text-5xl th-text-muted group-hover:text-accent transition-colors duration-150">
                          {artist.artistName?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <div className="p-4">
                        <div className="font-mono text-[13px] font-bold uppercase tracking-[0.1em] th-text group-hover:text-accent transition-colors duration-150">
                          {artist.artistName}
                        </div>
                        {artist.genres && artist.genres.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {artist.genres.slice(0, 2).map((genre) => (
                              <span key={genre.id} className="th-badge th-border">
                                {genre.name}
                              </span>
                            ))}
                          </div>
                        )}
                        {artist.location?.city && (
                          <div className="th-mono-meta mt-2 normal-case">
                            {artist.location.city}
                            {artist.location.country ? ` · ${artist.location.country}` : ''}
                            {showDistance && artist.distanceKm != null ? ` · ${artist.distanceKm} km` : ''}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-10">
                    <button
                      onClick={() =>
                        setSearchParams(
                          new URLSearchParams({
                            ...Object.fromEntries(searchParams),
                            page: Math.max(1, page - 1).toString(),
                          })
                        )
                      }
                      disabled={page === 1}
                      className="th-btn disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      ← Précédent
                    </button>
                    <span className="px-4 py-2 th-text-muted text-sm font-mono">
                      {page} / {totalPages}
                    </span>
                    <button
                      onClick={() =>
                        setSearchParams(
                          new URLSearchParams({
                            ...Object.fromEntries(searchParams),
                            page: Math.min(totalPages, page + 1).toString(),
                          })
                        )
                      }
                      disabled={page === totalPages}
                      className="th-btn disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Suivant →
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
